import { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from './AuthContext';
import { IconArrowLeft } from './Icons';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30;
const EXPIRES_IN_SECONDS = 10 * 60;

export default function OtpVerifyForm({ email, onVerified, onBack }) {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(EXPIRES_IN_SECONDS);
  const inputRefs = useRef([]);
  const { verifyOtp, resendOtp } = useContext(AuthContext);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const code = digits.join('');

  const submitCode = async (fullCode) => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const user = await verifyOtp(email, fullCode);
      onVerified(user);
    } catch (err) {
      setError(err.message);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (i, rawValue) => {
    const value = rawValue.replace(/\D/g, '');
    if (!value) {
      const next = [...digits];
      next[i] = '';
      setDigits(next);
      return;
    }
    // Handles multi-char input in one box (autofill / fast typing)
    const chars = value.slice(0, OTP_LENGTH - i).split('');
    const next = [...digits];
    chars.forEach((c, idx) => { next[i + idx] = c; });
    setDigits(next);
    const lastIdx = Math.min(i + chars.length, OTP_LENGTH - 1);
    inputRefs.current[lastIdx]?.focus();
    if (next.every((d) => d)) submitCode(next.join(''));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    const next = Array(OTP_LENGTH).fill('');
    text.split('').forEach((c, idx) => { next[idx] = c; });
    setDigits(next);
    inputRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
    if (next.every((d) => d)) submitCode(next.join(''));
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setResending(true);
    try {
      await resendOtp(email);
      setInfo('A new code has been sent to your email.');
      setCooldown(RESEND_COOLDOWN);
      setSecondsLeft(EXPIRES_IN_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  const mm = Math.floor(secondsLeft / 60);
  const ss = (secondsLeft % 60).toString().padStart(2, '0');

  return (
    <div className="otp-form">
      <p className="otp-sub">We sent a 6-digit code to <strong>{email}</strong>. Enter it below to verify your account.</p>

      <form
        className="otp-digit-row"
        onPaste={handlePaste}
        onSubmit={(e) => { e.preventDefault(); if (code.length === OTP_LENGTH) submitCode(code); }}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className={`otp-digit-box${error ? ' otp-digit-box-err' : ''}`}
            disabled={loading}
            aria-label={`Digit ${i + 1} of verification code`}
          />
        ))}
      </form>

      {error && <div className="auth-error">{error}</div>}
      {info && <div className="otp-info">{info}</div>}
      {loading && <p className="otp-verifying">Verifying…</p>}

      <p className="otp-expiry">
        {secondsLeft > 0
          ? <>Code expires in <strong>{mm}:{ss}</strong></>
          : <span className="otp-expired">Code expired — please resend.</span>}
      </p>

      <button
        type="button"
        className="btn submit otp-verify-btn"
        onClick={() => code.length === OTP_LENGTH && submitCode(code)}
        disabled={loading || code.length !== OTP_LENGTH}
      >
        {loading ? 'Verifying…' : 'Verify Email'}
      </button>

      <button type="button" className="otp-resend-btn" onClick={handleResend} disabled={resending || cooldown > 0}>
        {cooldown > 0 ? `Resend code in ${cooldown}s` : resending ? 'Sending…' : "Didn't get a code? Resend"}
      </button>
      {onBack && <button type="button" className="otp-back-btn" onClick={onBack}><IconArrowLeft size={14} /> Back</button>}
    </div>
  );
}
